/**
 * Gameplay Scene - Main game scene with terrain and entities
 */

import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { 
    SceneBase, 
    Engine, 
    TerrainManager,
    Camera,
    AssetManager,
    EntityManager,
    Entity,
    SpawnManager,
    PhysicsManager,
    TextureUtils,
    EnvironmentManager,
    FlatSpawnArea,
    NPCController,
    MapAssetManager,
    NPCPatrolPoint,
    DebugMenu,
    GroundCollision,
    ProceduralAnimationBuilder,
    SkeletonUtils,
} from "@engine/index";
import { analyzeModel, printModelAnalysis } from "@engine/assets/ModelImportUtils";
import { GameAssets } from "../assets/AssetCatalog";
import { registerAllMapAssets } from "../assets/MapAssetsCatalog";

// Required side effects for shadows
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";

export class GameplayScene extends SceneBase {
    private _terrain: TerrainManager | null = null;
    private _camera: Camera | null = null;
    private _sunLight: DirectionalLight | null = null;
    private _ambientLight: HemisphericLight | null = null;
    private _shadowGenerator: ShadowGenerator | null = null;
    
    // New systems
    private _assetManager: AssetManager | null = null;
    private _entityManager: EntityManager | null = null;
    private _spawnManager: SpawnManager | null = null;
    private _physicsManager: PhysicsManager | null = null;
    private _environment: EnvironmentManager | null = null;
    private _mapAssetManager: MapAssetManager | null = null;
    private _groundCollision: GroundCollision | null = null;
    private _debugMenu: DebugMenu | null = null;
    
    // Test entity
    private _sasquatch: Entity | null = null;
    private _sasquatchController: NPCController | null = null;

    constructor(engine: Engine) {
        super(engine);
    }

    protected async onInitialize(): Promise<void> {
        // Configure scene
        this._scene.clearColor = new Color4(0.5, 0.7, 0.9, 1.0); // Sky blue
        this._scene.ambientColor = new Color3(0.3, 0.3, 0.35);

        // Create lighting
        this._createLighting();

        // Initialize environment manager (day/night cycle, skybox)
        this._environment = new EnvironmentManager(this._scene, {
            initialTime: 10, // Start at 10:00 AM
            timeScale: 120, // 1 real second = 2 game minutes (fast for testing)
            enableDayNightCycle: true,
            enableFog: true,
            fogMode: 3,
        });
        
        // Connect lights to environment manager for automatic time-based updates
        if (this._sunLight && this._ambientLight) {
            this._environment.setLights(this._sunLight, this._ambientLight);
        }
        
        // Create procedural skybox
        this._environment.createProceduralSkybox(1000);
        
        // Log time changes
        this._environment.addEventListener("periodChange", (_event, data) => {
            console.log(`Time of day changed: ${data.previousValue} -> ${data.currentValue}`);
            console.log(`Current time: ${this._environment?.getFormattedTime()}`);
        });

        // Initialize physics FIRST
        this._physicsManager = new PhysicsManager(this._scene);
        await this._physicsManager.initialize();

        // Initialize asset manager
        this._assetManager = new AssetManager(this._scene, "assets/");
        this._assetManager.setCatalog(GameAssets);

        // Initialize entity manager
        this._entityManager = new EntityManager(this._scene, this._assetManager);

        // Create camera
        this._camera = new Camera(
            this._scene,
            this._engine.canvas,
            {
                mode: "orbit",
                position: new Vector3(0, 30, -50),
                target: new Vector3(0, 0, 0),
                // Configure modes
                free: {
                    speed: 20,
                    sprintMultiplier: 2.5,
                },
                follow: {
                    distance: 30,
                    height: 12,
                    acceleration: 0.05,
                },
                fps: {
                    eyeHeight: 1.7,
                    sensitivity: 0.003,
                },
                orbit: {
                    minRadius: 10,
                    maxRadius: 100,
                    autoRotate: false,
                },
            }
        );

        // Define flat spawn areas for creatures
        const flatSpawnAreas: FlatSpawnArea[] = [
            {
                name: "sasquatch-spawn",
                centerX: 0,
                centerZ: 0,
                radius: 15,
                blendDistance: 8,
                shape: "circle",
            },
            {
                name: "north-clearing",
                centerX: 0,
                centerZ: 60,
                radius: 20,
                blendDistance: 10,
                shape: "circle",
            },
            {
                name: "south-clearing",
                centerX: 0,
                centerZ: -60,
                radius: 18,
                blendDistance: 8,
                shape: "circle",
            },
            {
                name: "east-meadow",
                centerX: 50,
                centerZ: 20,
                radius: 25,
                blendDistance: 12,
                shape: "circle",
            },
        ];

        // Create terrain with flat spawn areas
        this._terrain = new TerrainManager(this._scene, {
            width: 200,
            depth: 200,
            subdivisions: 128,
            minHeight: 0,
            maxHeight: 15, // Increased for more dramatic terrain variation
        });

        // Generate procedural terrain with flat areas for entity spawning
        const terrain = this._terrain.createProcedural(12345, flatSpawnAreas);
        terrain.receiveShadows = true;
        
        // Apply grass texture to terrain
        this._terrain.setTexture("assets/textures/grass.png", 30);

        // Setup terrain physics collision AFTER physics is initialized
        this._terrain.setupPhysics();
        console.log("Terrain physics collision enabled");
        console.log(`Created ${flatSpawnAreas.length} flat spawn areas for entity placement`);

        // Initialize spawn manager with terrain awareness
        this._spawnManager = new SpawnManager(this._scene, this._entityManager, {
            heightOffset: 0, // Entity pivot at ground level
            useRaycast: true,
        });
        this._spawnManager.setTerrain(this._terrain);

        // Initialize map asset manager for static objects (trees, rocks, etc.)
        this._mapAssetManager = new MapAssetManager(this._scene, "assets/");
        this._mapAssetManager.setTerrain(this._terrain);
        registerAllMapAssets(this._mapAssetManager);

        // Initialize ground collision system for entities
        this._groundCollision = new GroundCollision(this._scene, {
            maxRayDistance: 100,
            rayOffset: 2,
            maxSlopeAngle: 45,
        });
        this._groundCollision.setTerrainMesh(terrain, this._terrain.physicsAggregate ?? undefined);

        // Initialize debug menu system
        this._initializeDebugMenu();

        // Setup fog for atmosphere
        this._scene.fogMode = 3; // Exponential fog
        this._scene.fogDensity = 0.003;
        this._scene.fogColor = new Color3(0.5, 0.7, 0.9);

        // Analyze the Sasquatch model first (dev tool - can be removed in production)
        await this._analyzeModelForDev();

        // Load Sasquatch entity
        await this._loadSasquatch();

        // Place trees around the map
        await this._placeMapAssets();

        console.log("GameplayScene initialized");
    }

    /**
     * Place map assets (trees, rocks, etc.) around the terrain
     */
    private async _placeMapAssets(): Promise<void> {
        if (!this._mapAssetManager || !this._terrain) return;

        try {
            console.log("Placing map assets...");

            // Place trees randomly around the map, avoiding flat spawn areas
            // Get the terrain bounds
            const terrainWidth = 200;
            const terrainDepth = 200;
            const halfWidth = terrainWidth / 2;
            const halfDepth = terrainDepth / 2;

            // Define areas to avoid (flat spawn areas where NPCs spawn) - smaller to allow more trees
            const avoidAreas = [
                { x: 0, z: 0, radius: 10 },      // sasquatch-spawn (smaller)
                { x: 0, z: 60, radius: 12 },     // north-clearing (smaller)
                { x: 0, z: -60, radius: 12 },    // south-clearing (smaller)
                { x: 50, z: 20, radius: 15 },    // east-meadow (smaller)
            ];

            // Generate tree positions avoiding spawn areas
            const treePositions: Vector3[] = [];
            const treeCount = 400; // More trees for dense forest
            const minTreeDistance = 2; // Even closer together for forest vibe
            let attempts = 0;
            const maxAttempts = treeCount * 30;

            while (treePositions.length < treeCount && attempts < maxAttempts) {
                attempts++;

                // Random position within terrain bounds (with margin)
                const margin = 10;
                const x = -halfWidth + margin + Math.random() * (terrainWidth - margin * 2);
                const z = -halfDepth + margin + Math.random() * (terrainDepth - margin * 2);

                // Check if position is in an avoid area
                const inAvoidArea = avoidAreas.some(area => {
                    const dx = x - area.x;
                    const dz = z - area.z;
                    return Math.sqrt(dx * dx + dz * dz) < area.radius;
                });

                if (inAvoidArea) continue;

                // Check minimum distance from other trees
                const tooClose = treePositions.some(pos => {
                    const dx = pos.x - x;
                    const dz = pos.z - z;
                    return Math.sqrt(dx * dx + dz * dz) < minTreeDistance;
                });

                if (tooClose) continue;

                // Get terrain height at this position
                const terrainHeight = this._terrain.getHeightAtPosition(x, z);
                
                // Allow trees at any reasonable height (terrain max is 3)
                if (terrainHeight < -1) continue;

                treePositions.push(new Vector3(x, terrainHeight, z));
            }

            // Place trees using batch placement
            const trees = await this._mapAssetManager.placeBatch({
                assetId: "tree",
                positions: treePositions,
                randomizeRotation: true,
                scaleRange: [0.8, 1.3], // Random scale variation
                adjustToTerrain: true,
            });

            // Add trees to shadow generator
            if (this._shadowGenerator) {
                this._mapAssetManager.addShadowCasters(this._shadowGenerator, "tree");
            }

            console.log(`Placed ${trees.length} trees on the map`);

            // Log collision info
            const treesWithCollision = trees.filter(t => t.physicsAggregate !== null).length;
            console.log(`  Trees with collision: ${treesWithCollision}`);
            
            // Debug: Log first tree collision position
            if (trees.length > 0 && trees[0].physicsAggregate) {
                const firstTree = trees[0];
                const aggregate = firstTree.physicsAggregate;
                if (aggregate) {
                    const collisionMesh = aggregate.body?.transformNode;
                    if (collisionMesh) {
                        const worldPos = collisionMesh.getAbsolutePosition();
                        console.log(`  First tree collision at: (${worldPos.x.toFixed(2)}, ${worldPos.y.toFixed(2)}, ${worldPos.z.toFixed(2)})`);
                        console.log(`  First tree root at: (${firstTree.rootNode.position.x.toFixed(2)}, ${firstTree.rootNode.position.y.toFixed(2)}, ${firstTree.rootNode.position.z.toFixed(2)})`);
                    }
                }
            }

        } catch (error) {
            console.error("Failed to place map assets:", error);
        }
    }

    /**
     * Analyze model and print info (development helper)
     * This helps determine correct scale and physics settings
     */
    private async _analyzeModelForDev(): Promise<void> {
        try {
            // Analyze the model to get its actual dimensions
            const analysis = await analyzeModel(
                this._scene,
                "assets/entities/Sasquatch/Sasquatch.glb",
                3 // Target height of 3 meters for a Sasquatch
            );
            printModelAnalysis(analysis, "Sasquatch");
        } catch (error) {
            console.warn("Could not analyze model:", error);
        }
    }

    /**
     * Load and setup the Sasquatch entity
     */
    private async _loadSasquatch(): Promise<void> {
        try {
            console.log("Loading Sasquatch model...");
            
            // Get spawn position from flat spawn area for level terrain
            const spawnArea = this._terrain!.getFlatArea("sasquatch-spawn");
            let spawnX = 0;
            let spawnZ = 0;
            
            if (spawnArea) {
                // Spawn at center of the flat area
                spawnX = spawnArea.centerX;
                spawnZ = spawnArea.centerZ;
                console.log(`Using flat spawn area "${spawnArea.name}" at (${spawnX}, ${spawnZ})`);
            }
            
            const terrainHeight = this._terrain!.getHeightAtPosition(spawnX, spawnZ);
            console.log(`Terrain height at (${spawnX}, ${spawnZ}): ${terrainHeight.toFixed(2)}`);
            
            // Use SpawnManager for terrain-aware spawning
            // This automatically places the entity at the correct terrain height
            this._sasquatch = await this._spawnManager!.spawnOnTerrain({
                name: "Sasquatch",
                modelId: "sasquatch",
                position: new Vector3(spawnX, 0, spawnZ), // X/Z position, Y will be adjusted to terrain
                tags: ["cryptid", "npc"],
                autoInitialize: true,
                autoPhysics: false, // Disable for now until physics tuning
                autoAnimation: false,
            });

            // Debug: Log mesh info
            console.log(`Sasquatch meshes: ${this._sasquatch.meshes.length}`);
            this._sasquatch.meshes.forEach((mesh, i) => {
                console.log(`  Mesh ${i}: "${mesh.name}", vertices: ${mesh.getTotalVertices()}, enabled: ${mesh.isEnabled()}`);
            });

            // After spawning, adjust Y so the entity's feet are on the ground
            // The entity's bounding box bottom should touch the terrain
            const bbox = this._sasquatch.getBoundingBox();
            const entityHeight = bbox.size.y;
            const bottomY = bbox.min.y;
            const currentY = this._sasquatch.position.y;
            
            // Log positioning info for debugging
            console.log(`Entity bounding box:`);
            console.log(`  Min: (${bbox.min.x.toFixed(2)}, ${bbox.min.y.toFixed(2)}, ${bbox.min.z.toFixed(2)})`);
            console.log(`  Max: (${bbox.max.x.toFixed(2)}, ${bbox.max.y.toFixed(2)}, ${bbox.max.z.toFixed(2)})`);
            console.log(`  Size: (${bbox.size.x.toFixed(2)}, ${bbox.size.y.toFixed(2)}, ${bbox.size.z.toFixed(2)})`);
            console.log(`  Height: ${entityHeight.toFixed(2)}, bottomY: ${bottomY.toFixed(2)}, posY: ${currentY.toFixed(2)}`);
            
            // Only adjust Y if we got valid bounding box
            if (entityHeight > 0) {
                // setBottomY places the entity so its bounding box bottom is at terrain level
                this._sasquatch.setBottomY(terrainHeight);
                console.log(`Adjusted Sasquatch Y position. New pos: ${this._sasquatch.position.y.toFixed(2)}`);
            } else {
                // Fallback - just place at terrain height
                this._sasquatch.position.y = terrainHeight;
                console.warn(`Could not get valid bounding box, placing at terrain height directly`);
            }

            // Setup shadow casting
            if (this._shadowGenerator && this._sasquatch.meshes.length > 0) {
                this._sasquatch.meshes.forEach(mesh => {
                    if (mesh.getTotalVertices() > 0) {
                        this._shadowGenerator!.addShadowCaster(mesh);
                        mesh.receiveShadows = true;
                    }
                });
            }

            // Log available animations
            const animations = this._sasquatch.getAnimationNames();
            console.log("Available Sasquatch animations:", animations);

            // =========================================
            // PROCEDURAL ANIMATIONS - Create if model has none
            // =========================================
            // If the model has a skeleton but no animations, create procedural ones
            const skeleton = this._sasquatch.skeleton;
            if (skeleton && animations.length === 0) {
                console.log("Model has skeleton but no animations - creating procedural animations...");
                
                // Print skeleton hierarchy for reference
                SkeletonUtils.printHierarchy(skeleton);
                
                // Create procedural animation builder
                const animBuilder = new ProceduralAnimationBuilder(
                    skeleton,
                    this._sasquatch.transformNode,
                    this._scene
                );
                
                // Create basic movement animations
                const idleAnim = animBuilder.createBreathingAnimation();
                const walkAnim = animBuilder.createWalkAnimation();
                const runAnim = animBuilder.createRunAnimation();
                const lookAnim = animBuilder.createLookAroundAnimation();
                
                // Add the animations to the entity
                const animGroups = (this._sasquatch as any)._animationGroups as any[];
                animGroups.push(idleAnim, walkAnim, runAnim, lookAnim);
                
                console.log("Created procedural animations:", [idleAnim, walkAnim, runAnim, lookAnim].map(a => a.name));
            }

            // Analyze textures on the model (dev tool)
            console.log("\n--- Texture Analysis ---");
            TextureUtils.printTextureAnalysis(this._sasquatch.meshes);
            console.log("--- End Texture Analysis ---\n");

            // =========================================
            // NPC CONTROLLER - Animation + Movement
            // =========================================
            // Create NPC controller for automatic animation management
            this._sasquatchController = new NPCController(this._sasquatch, this._scene, {
                // Map animation names from GLB to movement states
                // The controller auto-detects common names like "walk", "idle", "run"
                animations: {
                    // Override auto-detection if needed:
                    // idle: "Armature|Idle",
                    // walk: "Armature|Walk", 
                    // run: "Armature|Run",
                },
                locomotion: {
                    walkSpeed: 4.0,      // Units per second when walking
                    runSpeed: 10.0,      // Units per second when running (faster!)
                    rotationSpeed: 8.0,  // Radians per second for turning
                    smoothRotation: true, // Interpolate rotation smoothly
                },
                syncAnimationSpeed: true, // Animation speed matches movement speed
                blendTime: 0.2,           // Blend time between animations (seconds)
                // Provide terrain height callback so NPC follows terrain surface
                getGroundHeight: (x: number, z: number) => {
                    return this._terrain?.getHeightAtPosition(x, z) ?? 0;
                },
            });

            // Listen to NPC state changes
            this._sasquatchController.on("stateChange", (event) => {
                console.log(`Sasquatch state: ${event.previousState} -> ${event.currentState}`);
            });

            // Example: Make Sasquatch patrol between flat areas
            this._setupSasquatchPatrol();

            console.log("NPC Controller initialized for Sasquatch");
            console.log(`  Available animations: ${this._sasquatch.getAnimationNames().join(", ")}`);
            console.log(`  Auto-detected mappings applied`);

            // NOTE: Physics disabled for NPC - kinematic physics conflicts with transform-based movement
            // The NPCController moves the entity via transform node, but physics would override mesh position
            // For collision detection, we'll use raycasting or manual collision checks instead
            // this._sasquatch.setupPhysics(
            //     ColliderShape.CAPSULE,
            //     0, // mass = 0 for kinematic (NPC that doesn't fall)
            //     {
            //         friction: 0.6,
            //         restitution: 0.1,
            //         isKinematic: true, // NPCs are kinematic - controlled by code, not physics
            //     }
            // );
            console.log("Sasquatch physics DISABLED (using transform-based movement)");

            // Setup Camera with Sasquatch as follow target
            if (this._camera) {
                // Set Sasquatch's transform node as the follow target
                this._camera.setFollowTarget(this._sasquatch.transformNode);
                this._camera.setMode("follow");
                console.log(`Camera set to follow mode, tracking Sasquatch`);
                
                // Update debug menu display
                if (this._debugMenu) {
                    this._debugMenu.updateCameraTargetDisplay("Sasquatch");
                }
            }

            console.log("Sasquatch loaded successfully!");
            
        } catch (error) {
            console.error("Failed to load Sasquatch:", error);
        }
    }

    /**
     * Setup Sasquatch to patrol between flat spawn areas
     * Demonstrates NPC controller patrol and movement features
     */
    private _setupSasquatchPatrol(): void {
        if (!this._sasquatchController || !this._terrain) {
            console.error("Cannot setup patrol - missing controller or terrain");
            return;
        }

        console.log("=== SETTING UP SASQUATCH PATROL ===");
        console.log(`Sasquatch current position: (${this._sasquatch!.position.x.toFixed(2)}, ${this._sasquatch!.position.y.toFixed(2)}, ${this._sasquatch!.position.z.toFixed(2)})`);

        // Get flat areas for patrol points
        const spawnArea = this._terrain.getFlatArea("sasquatch-spawn");
        const northArea = this._terrain.getFlatArea("north-clearing");
        const eastArea = this._terrain.getFlatArea("east-meadow");
        
        console.log("Spawn area:", spawnArea);
        console.log("North area:", northArea);
        console.log("East area:", eastArea);
        
        if (!spawnArea) {
            console.error("No spawn area found!");
            return;
        }

        // Build patrol route from flat areas
        const patrolPoints: NPCPatrolPoint[] = [];

        // Home position
        const homeY = this._terrain.getHeightAtPosition(spawnArea.centerX, spawnArea.centerZ);
        patrolPoints.push({
            position: new Vector3(spawnArea.centerX, homeY, spawnArea.centerZ),
            waitTime: 2,
            run: false,  // Walk to this point
            onReached: () => console.log("Sasquatch reached home area"),
        });

        // North clearing (if exists)
        if (northArea) {
            const northY = this._terrain.getHeightAtPosition(northArea.centerX, northArea.centerZ);
            patrolPoints.push({
                position: new Vector3(northArea.centerX, northY, northArea.centerZ),
                waitTime: 2,
                run: false,  // Walk to this point
                onReached: () => console.log("Sasquatch reached north clearing"),
            });
        }

        // East meadow (if exists)
        if (eastArea) {
            const eastY = this._terrain.getHeightAtPosition(eastArea.centerX, eastArea.centerZ);
            patrolPoints.push({
                position: new Vector3(eastArea.centerX, eastY, eastArea.centerZ),
                waitTime: 2,
                run: false,  // Walk to this point
                onReached: () => console.log("Sasquatch reached east meadow"),
            });
        }

        console.log(`Built ${patrolPoints.length} patrol points`);

        // Start patrol if we have points
        if (patrolPoints.length > 1) {
            // Remove the first point if NPC is already there (spawn location)
            // This prevents the "already at target" issue
            const currentPos = this._sasquatch!.position;
            const firstPoint = patrolPoints[0].position;
            const distToFirst = Math.sqrt(
                Math.pow(currentPos.x - firstPoint.x, 2) + 
                Math.pow(currentPos.z - firstPoint.z, 2)
            );
            
            console.log(`Sasquatch spawn distance to first patrol point: ${distToFirst.toFixed(2)}`);
            
            // If already very close to first point, start from the second point
            if (distToFirst < 2.0 && patrolPoints.length > 1) {
                console.log("Skipping first patrol point (already at spawn)");
                // Move first point to end so we still visit it in the loop
                const firstPointData = patrolPoints.shift()!;
                patrolPoints.push(firstPointData);
            }
            
            this._sasquatchController.patrol(patrolPoints, true); // Loop forever
            console.log(`Sasquatch patrolling ${patrolPoints.length} points`);
            console.log("=== PATROL SETUP COMPLETE ===");
        } else {
            // Just stand idle if no patrol points
            console.log("No patrol points available, Sasquatch will idle");
        }
    }

    /**
     * Initialize the debug menu with actions and toggles
     */
    private _initializeDebugMenu(): void {
        this._debugMenu = new DebugMenu(this._scene, {
            visible: true,
            position: "top-left",
            toggleKey: "F3",
            theme: "dark",
            showStats: true,
            enableShortcuts: true,
        });

        // Register debug actions
        this._debugMenu.registerAction("toggle-collision-debug", "Toggle Collision Viz", () => {
            if (this._mapAssetManager) {
                const currentMode = this._mapAssetManager.debugMode;
                this._mapAssetManager.setDebugMode(!currentMode);
                console.log(`Map asset collision debug: ${!currentMode ? 'ON' : 'OFF'}`);
            }
        }, "physics", "C");

        this._debugMenu.registerAction("teleport-spawn", "Teleport to Spawn", () => {
            if (this._sasquatch && this._terrain) {
                const spawnArea = this._terrain.getFlatArea("sasquatch-spawn");
                if (spawnArea) {
                    const y = this._terrain.getHeightAtPosition(spawnArea.centerX, spawnArea.centerZ);
                    this._sasquatch.position = new Vector3(spawnArea.centerX, y, spawnArea.centerZ);
                    console.log("Teleported Sasquatch to spawn area");
                }
            }
        }, "entity", "T");

        this._debugMenu.registerAction("toggle-physics-debug", "Toggle Physics Debug", () => {
            if (this._physicsManager) {
                const current = this._physicsManager.debugEnabled;
                this._physicsManager.setDebugEnabled(!current);
                console.log(`Physics debug: ${!current ? 'ON' : 'OFF'}`);
            }
        }, "physics", "P");

        this._debugMenu.registerAction("test-raycast", "Test Raycast from NPC", () => {
            if (this._sasquatch && this._scene) {
                const physicsEngine = this._scene.getPhysicsEngine();
                if (physicsEngine) {
                    const pos = this._sasquatch.position;
                    // Cast rays in 8 directions
                    const directions = [
                        new Vector3(1, 0, 0),
                        new Vector3(-1, 0, 0),
                        new Vector3(0, 0, 1),
                        new Vector3(0, 0, -1),
                        new Vector3(0.707, 0, 0.707),
                        new Vector3(-0.707, 0, 0.707),
                        new Vector3(0.707, 0, -0.707),
                        new Vector3(-0.707, 0, -0.707),
                    ];
                    
                    console.log(`=== Raycast test from NPC at (${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)}) ===`);
                    
                    for (const dir of directions) {
                        const rayOrigin = pos.add(new Vector3(0, 1.0, 0)); // 1m above ground
                        const rayEnd = rayOrigin.add(dir.scale(20)); // 20m ray
                        const result = physicsEngine.raycast(rayOrigin, rayEnd);
                        
                        if (result.hasHit) {
                            const hitName = result.body?.transformNode?.name ?? "unknown";
                            console.log(`  Dir (${dir.x.toFixed(1)}, ${dir.z.toFixed(1)}): HIT "${hitName}" at ${result.hitDistance?.toFixed(2)}m`);
                        } else {
                            console.log(`  Dir (${dir.x.toFixed(1)}, ${dir.z.toFixed(1)}): no hit`);
                        }
                    }
                }
            }
        }, "physics", "R");

        this._debugMenu.registerAction("slow-motion", "Toggle Slow-Mo", () => {
            if (this._physicsManager) {
                this._physicsManager.toggleSlowMotion(0.25);
                console.log(`Slow motion: ${this._physicsManager.timeScale < 1 ? 'ON' : 'OFF'}`);
            }
        }, "physics", "M");

        this._debugMenu.registerAction("pause-time", "Pause Time", () => {
            if (this._physicsManager) {
                this._physicsManager.togglePause();
                console.log(`Time: ${this._physicsManager.isPaused ? 'PAUSED' : 'RUNNING'}`);
            }
        }, "physics");

        this._debugMenu.registerAction("log-position", "Log Entity Position", () => {
            if (this._sasquatch) {
                const pos = this._sasquatch.position;
                console.log(`Sasquatch Position: (${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})`);
            }
        }, "entity");

        this._debugMenu.registerAction("stop-patrol", "Stop Patrol", () => {
            if (this._sasquatchController) {
                this._sasquatchController.stopPatrol();
                console.log("Patrol stopped");
            }
        }, "entity");

        this._debugMenu.registerAction("resume-patrol", "Resume Patrol", () => {
            if (this._sasquatchController) {
                this._setupSasquatchPatrol();
                console.log("Patrol resumed");
            }
        }, "entity");

        // Note: Camera uses EventEmitter - connect to debug menu manually if needed
        // The debug menu's CameraModeManager integration is legacy
        // For Camera, use event listeners directly:
        if (this._camera) {
            this._camera.on("modeChanged", (event) => {
                console.log(`Camera mode changed: ${event.previous} -> ${event.current}`);
            });
        }

        // Listen to toggle events and connect to actual systems
        this._debugMenu.onToggle((category, enabled) => {
            console.log(`Debug toggle: ${category} = ${enabled}`);
            
            switch (category) {
                case "collision-shapes":
                    // Toggle map asset collision visualization
                    if (this._mapAssetManager) {
                        this._mapAssetManager.setDebugMode(enabled);
                    }
                    break;
                case "physics-bodies":
                    // Toggle physics debug visualization
                    if (this._physicsManager) {
                        this._physicsManager.setDebugEnabled(enabled);
                    }
                    break;
                case "terrain-grid":
                    // Toggle terrain wireframe grid
                    if (enabled && this._terrain && this._debugMenu) {
                        this._debugMenu.visualizer.showTerrainGrid(this._terrain, 5, 0.1);
                    } else if (this._debugMenu) {
                        this._debugMenu.visualizer.hideTerrainGrid();
                    }
                    break;
                case "bounding-boxes":
                    // Toggle entity bounding boxes and NPC collision shape
                    if (this._sasquatch && this._debugMenu) {
                        if (enabled) {
                            this._debugMenu.visualizer.showEntityBounds(this._sasquatch);
                            // Show NPC collision radius for obstacle avoidance
                            if (this._sasquatchController) {
                                this._debugMenu.visualizer.showNPCCollisionRadius(
                                    "sasquatch",
                                    this._sasquatch,
                                    this._sasquatchController.obstacleCheckRadius,
                                    this._sasquatchController.obstacleRayHeights
                                );
                            }
                        } else {
                            this._debugMenu.visualizer.hideEntityBounds(this._sasquatch);
                            this._debugMenu.visualizer.hideNPCCollisionRadius("sasquatch");
                        }
                    }
                    break;
                case "debug-enabled":
                    // Global debug toggle - only controls the DebugVisualizer master enable
                    // Individual debug options (collision shapes, physics, etc.) are controlled
                    // by their own toggles and should NOT be automatically enabled here
                    // The visualizer.setEnabled() controls visibility of ALL debug visualizations
                    // that have been explicitly enabled via their individual toggles
                    break;
            }
        });

        // Listen to action events
        this._debugMenu.onAction((actionId) => {
            console.log(`Debug action executed: ${actionId}`);
        });

        console.log("Debug menu initialized (Press F3 to toggle)");
    }

    /**
     * Create scene lighting
     */
    private _createLighting(): void {
        // Ambient light (hemisphere)
        this._ambientLight = new HemisphericLight(
            "ambientLight",
            new Vector3(0, 1, 0),
            this._scene
        );
        this._ambientLight.intensity = 0.4;
        this._ambientLight.groundColor = new Color3(0.2, 0.2, 0.25);
        this._ambientLight.diffuse = new Color3(0.9, 0.9, 0.95);

        // Sun directional light
        this._sunLight = new DirectionalLight(
            "sunLight",
            new Vector3(-0.5, -1, 0.5),
            this._scene
        );
        this._sunLight.intensity = 0.8;
        this._sunLight.diffuse = new Color3(1, 0.95, 0.85);
        this._sunLight.specular = new Color3(1, 0.95, 0.85);
        this._sunLight.position = new Vector3(50, 100, -50);

        // Shadow generator
        this._shadowGenerator = new ShadowGenerator(2048, this._sunLight);
        this._shadowGenerator.useBlurExponentialShadowMap = true;
        this._shadowGenerator.blurKernel = 32;
        this._shadowGenerator.darkness = 0.3;
    }

    protected onUpdate(deltaTime: number): void {
        // Update environment (day/night cycle)
        if (this._environment) {
            this._environment.update(deltaTime);
        }

        // Update unified camera
        if (this._camera) {
            this._camera.update(deltaTime);
        }

        // Update all entities
        if (this._entityManager) {
            this._entityManager.update(deltaTime);
        }

        // Update NPC controller (handles movement, rotation, animation)
        if (this._sasquatchController) {
            this._sasquatchController.update(deltaTime);
        }

        // Update physics manager (time scaling)
        if (this._physicsManager) {
            this._physicsManager.update(deltaTime);
        }

        // Update ground collision system
        if (this._groundCollision) {
            this._groundCollision.update(deltaTime);
        }

        // Update debug menu (stats display)
        if (this._debugMenu) {
            this._debugMenu.update(deltaTime);
            // Update entity count
            const entityCount = this._entityManager?.activeEntityCount ?? 0;
            this._debugMenu.setEntityCount(entityCount);
            // Update collision count from map assets
            const collisionCount = this._mapAssetManager?.instanceCount ?? 0;
            this._debugMenu.setCollisionCount(collisionCount);
        }
        
        // Debug: Log Sasquatch position periodically
        if (this._sasquatch && Math.random() < 0.005) {
            const pos = this._sasquatch.position;
            console.log(`[onUpdate] Sasquatch position: (${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})`);
        }
    }

    protected onDispose(): void {
        // Dispose debug menu
        if (this._debugMenu) {
            this._debugMenu.dispose();
        }
        
        // Dispose ground collision system
        if (this._groundCollision) {
            this._groundCollision.dispose();
        }
        
        if (this._sasquatchController) {
            this._sasquatchController.dispose();
        }
        if (this._mapAssetManager) {
            this._mapAssetManager.dispose();
        }
        if (this._environment) {
            this._environment.dispose();
        }
        if (this._spawnManager) {
            this._spawnManager.dispose();
        }
        if (this._entityManager) {
            this._entityManager.dispose();
        }
        if (this._assetManager) {
            this._assetManager.dispose();
        }
        if (this._terrain) {
            this._terrain.dispose();
        }
        if (this._physicsManager) {
            this._physicsManager.dispose();
        }
        if (this._camera) {
            this._camera.dispose();
        }
    }
}
